import { IonInput } from '@ionic/react';
import styled from 'styled-components';
import { AllieButtonCorner } from '../../Buttons/Button/Button.types';
import { AllieInputProps, AllieInputSize, AllieInputType } from './Input.types';

type InternalButtonState = 'initial' | 'active' | 'hover' | 'focus';

const textColor = {
  active: 60,
  hover: 60,
  focus: 60,
  initial: 60,
  disabled: 50,
};

const backgroundColor = {
  primary: 10,
  secondary: 10,
  warning: 10,
  error: 10,
};

const colorByType = {
  primary: 'neutral',
  secondary: 'primary',
  warning: 'warning',
  error: 'error',
};

const borderByType = {
  primary: 30,
  secondary: 60,
  warning: 50,
  error: 50,
};

const verticalPadding = {
  normal: 18,
  medium: 16,
  small: 8,
};

const defaults: AllieInputParams = {
  allieType: 'primary',
  allieSize: 'normal',
  corner: 'rounded',
  border: true,
  disabled: false,
  shadow: false,
};

const getBackground = (allieType = defaults.allieType, disabled = defaults.disabled) => {
  if (allieType === 'secondary') return 'transparent';
  if (allieType === 'primary') return 'white';
  if (disabled) return `${colorByType[allieType]}-${backgroundColor[allieType]}`;

  return `${colorByType[allieType]}-${backgroundColor[allieType]}`;
};

const getColor = (state: InternalButtonState, disabled = false) => {
  if (disabled) return `neutral-${textColor.disabled}`;

  return `neutral-${textColor[state]}`;
};

const getBorder = (allieType = defaults.allieType, border = defaults.border) =>
  allieType === 'secondary' || border
    ? `${colorByType[allieType]}-${borderByType[allieType]}`
    : 'transparent';

const getOutline = (allieType = defaults.allieType) => `${colorByType[allieType]}-50`;

interface AllieInputParams {
  allieType: AllieInputType;
  disabled: boolean;
  border: boolean;
  shadow: boolean;
  corner: AllieButtonCorner;
  allieSize: AllieInputSize;
}

export const StyledItem = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['allieType', 'allieSize', 'corner', 'border', 'shadow'].includes(prop),
})<AllieInputProps>`
  display: flex;
  background: var(
    --allie-color-${(props: AllieInputProps) => getBackground(props.allieType, props.disabled)}
  );
  border-color: var(
    --allie-color-${(props: AllieInputProps) => getBorder(props.allieType, props.border)}
  );
  border-width: 1px;
  border-style: solid;
  border-radius: ${(props: AllieInputProps) => (props.corner === 'rounded' ? '25' : '8')}px;
  && {
    &:focus {
      outline: solid 3px
        var(--allie-color-${(props: AllieInputProps) => getOutline(props.allieType)});
    }
  }
  box-shadow: ${(props: AllieInputProps) =>
    props.shadow ? '0px 3px 10px rgba(0, 0, 0, 0.18)' : 'none'};
  width: -webkit-fill-available;

  &.input-with-suffix {
    position: relative;
  }
`;

export const StyledInput: any = styled(IonInput).withConfig({
  shouldForwardProp: (prop) =>
    !['allieType', 'allieSize', 'corner', 'border', 'shadow'].includes(prop),
})<AllieInputProps>`
  && {
    --background: var(
      --allie-color-${(props: AllieInputProps) => getBackground(props.allieType, props.disabled)}
    );
  }
  && {
    --background-activated: var(
      --allie-color-${(props: AllieInputProps) => getBackground(props.allieType, props.disabled)}
    );
  }
  && {
    --background-activated-opacity: 1;
  }
  && {
    --background-hover: var(
      --allie-color-${(props: AllieInputProps) => getBackground(props.allieType, props.disabled)}
    );
  }
  && {
    --background-hover-opacity: 1;
  }
  && {
    --background-focused: var(
      --allie-color-${(props: AllieInputProps) => getBackground(props.allieType, props.disabled)}
    );
  }
  && {
    --background-focused-opacity: 1;
  }
  && {
    --color: var(--allie-color-${(props: AllieInputProps) => getColor('initial', props.disabled)});
  }
  && {
    --color-activated: var(
      --allie-color-${(props: AllieInputProps) => getColor('active', props.disabled)}
    );
  }
  && {
    --color-hover: var(
      --allie-color-${(props: AllieInputProps) => getColor('hover', props.disabled)}
    );
  }
  && {
    --color-focused: var(
      --allie-color-${(props: AllieInputProps) => getColor('focus', props.disabled)}
    );
  }
  border-radius: ${(props: AllieInputProps) => (props.corner === 'rounded' ? '25' : '8')}px;
  && {
    &:focus {
      outline: solid 3px
        var(--allie-color-${(props: AllieInputProps) => getOutline(props.allieType)});
    }
  }
  box-sizing: border-box;
  text-transform: none;
  &&& {
    --padding-top: ${(props: AllieInputProps) => verticalPadding[props.allieSize || 'normal']}px;
  }
  &&& {
    --padding-bottom: ${(props: AllieInputProps) => verticalPadding[props.allieSize || 'normal']}px;
  }
  &&& {
    --padding-start: ${(props: AllieInputProps) => (props.allieSize === 'small' ? '15' : '40')}px;
  }
  &&& {
    --padding-end: ${(props: AllieInputProps) => (props.allieSize === 'small' ? '15' : '40')}px;
  }
`;

export const StyledSpan = styled('span')`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
`;
